import React, { createContext, useContext, useState, useEffect } from 'react';
import { useMediaQuery } from '../hooks/useMediaQuery';

const SidebarContext = createContext();

export const SidebarProvider = ({ children }) => {
  // ── Mobile detection ──
  const isMobile = useMediaQuery('(max-width: 767px)');

  // ── Desktop collapse state (persisted) ──
  const [isCollapsed, setIsCollapsed] = useState(() => {
    const saved = localStorage.getItem('sidebarCollapsed');
    return saved ? JSON.parse(saved) : false;
  });

  // ── Mobile overlay state ──
  const [isMobileOpen, setIsMobileOpen] = useState(false);

  // Auto-close mobile menu when switching to desktop
  useEffect(() => {
    if (!isMobile && isMobileOpen) {
      setIsMobileOpen(false);
    }
  }, [isMobile, isMobileOpen]);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    if (isMobile && isMobileOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isMobile, isMobileOpen]);

  // Close mobile menu on ESC key
  useEffect(() => {
    if (!isMobile || !isMobileOpen) return;
    const handleEsc = (e) => {
      if (e.key === 'Escape') setIsMobileOpen(false);
    };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [isMobile, isMobileOpen]);

  const toggleSidebar = () => {
    const newValue = !isCollapsed;
    setIsCollapsed(newValue);
    localStorage.setItem('sidebarCollapsed', JSON.stringify(newValue));
  };

  const toggleMobileMenu = () => {
    setIsMobileOpen(prev => !prev);
  };

  const closeMobileMenu = () => {
    setIsMobileOpen(false);
  };

  // ── Computed sidebar width ──
  // Mobile: 0 when closed (sidebar is overlay, doesn't push content)
  // Desktop: 68 collapsed, 232 expanded (estreitado de 260 p/ dar mais área à página)
  const sidebarWidth = isMobile ? 0 : (isCollapsed ? 68 : 232);

  return (
    <SidebarContext.Provider value={{
      isCollapsed,
      toggleSidebar,
      isMobile,
      isMobileOpen,
      toggleMobileMenu,
      closeMobileMenu,
      sidebarWidth,
    }}>
      {children}
    </SidebarContext.Provider>
  );
};

export const useSidebar = () => {
  const context = useContext(SidebarContext);
  if (!context) {
    throw new Error('useSidebar must be used within SidebarProvider');
  }
  return context;
};
